import { useEffect, useState } from 'react'
import { AlertTriangle, CheckCircle2, RotateCcw, Trash2 } from 'lucide-react'
import { useUpdateDraft } from '../../hooks/useDataCenterApi'
import {
  clearPendingLocalOperation,
  previewPendingLocalOperation,
  readPendingLocalOperation,
  type PendingLocalOperation,
} from '../../utils/localOperationRecovery.logic'

type Props = {
  code: string
  draftId: number | null
  currentRevision: number | null
}

export function DraftRecoveryBanner({ code, draftId, currentRevision }: Props) {
  const [pending, setPending] = useState<PendingLocalOperation | null>(null)
  const updateDraft = useUpdateDraft(code)

  const refresh = () => {
    if (draftId == null) return setPending(null)
    setPending(readPendingLocalOperation(window.localStorage, code, draftId))
  }

  useEffect(() => {
    refresh()
    window.addEventListener('dc-pending-operation-changed', refresh)
    return () => window.removeEventListener('dc-pending-operation-changed', refresh)
  }, [code, draftId])

  if (!pending) return null
  const preview = previewPendingLocalOperation(pending, currentRevision)
  const isConflict = preview.status === 'conflict'

  const replay = async () => {
    if (isConflict || updateDraft.isPending) return
    await updateDraft.mutateAsync({
      draftId: pending.draftId,
      expected_revision: pending.expectedRevision ?? undefined,
      racks_json: pending.racksJson,
      cable_trays_json: pending.cableTraysJson,
    })
    clearPendingLocalOperation(window.localStorage, code, pending.draftId, pending.id)
    window.location.reload()
  }

  const discard = () => clearPendingLocalOperation(window.localStorage, code, pending.draftId, pending.id)

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10, minHeight: 40, padding: '7px 12px',
      background: isConflict ? 'rgba(245, 158, 11, 0.12)' : 'rgba(34, 197, 94, 0.10)',
      borderBottom: `1px solid ${isConflict ? 'rgba(245, 158, 11, 0.35)' : 'rgba(34, 197, 94, 0.3)'}`,
      color: 'var(--text-primary, #e5e7eb)', fontSize: 12,
    }} role="status" aria-live={isConflict ? 'assertive' : 'polite'} className={`draft-recovery-banner${isConflict ? ' is-conflict' : ''}`}>
      {isConflict
        ? <AlertTriangle size={15} color="var(--status-warning)" aria-hidden="true" />
        : <CheckCircle2 size={15} color="var(--status-normal)" aria-hidden="true" />}
      <span style={{ flex: 1 }}>
        {isConflict
          ? `Найдена неподтверждённая операция, но черновик уже изменился (ревизия ${preview.expectedRevision ?? '—'} → ${preview.currentRevision ?? '—'}). Сначала проверьте конфликт через rebase.`
          : 'Найдена неподтверждённая операция после сбоя. Можно безопасно повторить её в текущем черновике.'}
      </span>
      <button type="button" onClick={replay} disabled={isConflict || updateDraft.isPending} aria-label="Повторить неподтверждённую операцию" title="Повторить операцию" style={buttonStyle(isConflict)}>
        <RotateCcw size={13} /> Повторить
      </button>
      <button type="button" onClick={discard} aria-label="Отбросить неподтверждённую операцию" title="Удалить локальную операцию" style={buttonStyle(false)}>
        <Trash2 size={13} /> Отбросить
      </button>
    </div>
  )
}

function buttonStyle(disabled: boolean): React.CSSProperties {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 5, border: '1px solid var(--border, #334155)',
    background: 'var(--bg-panel, #111827)', color: disabled ? 'var(--text-muted, #6b7280)' : 'var(--text-primary, #f3f4f6)',
    padding: '4px 7px', borderRadius: 4, fontSize: 11, cursor: disabled ? 'not-allowed' : 'pointer',
  }
}
